import React, {useState, useEffect, useMemo, useRef} from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer } from 'recharts';
import './ClimaLaboral.scss';
import {
    getHistoricData,
    getColaboradorData,
    getDepartamentoData,
    getGeneralData,
    individualOperatorAnalysis, getMoodByOperator, getOperatorMotivationTrend
} from "./Integracion";
import {SelectButton} from "primereact/selectbutton";
import {FilterMatchMode} from "primereact/api";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";

const ClimaLaboral = () => {
    // Estados para almacenar datos
    const [colaboradorData, setColaboradorData] = useState([]);
    const [departamentoData, setDepartamentoData] = useState([]);
    const [generalData, setGeneralData] = useState([]);
    const [transformedData, setTransformedData] = useState([]);
    const [historicoData, setHistoricoData] = useState([]);
    const [selectedColaborador, setSelectedColaborador] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [expandedRows, setExpandedRows] = useState(null);
    const [promedio, setPromedio] = useState(null);
    const [showOperator, setShowOperator] = useState(false);
    const dt = useRef(null);


    const show = [
        { name: 'Colaboradores', value: false },
        { name: 'Operadores', value: true }
    ];

    // Datos de ejemplo basados en tus queries SQL
    useEffect(() => {
        if (!showOperator) {
            getColaboradorData(setColaboradorData);
            getDepartamentoData(setDepartamentoData);
            getGeneralData(setGeneralData);
        } else {
            getMoodByOperator(setColaboradorData);
            getOperatorMotivationTrend(setGeneralData);
            setActiveIndex(0);
        }

    }, [showOperator]);

    useEffect(() => {
        if (colaboradorData.length > 0) {
            setSelectedColaborador(colaboradorData[0]);
            const estadoMasComun = obtenerEstadoPredominante(colaboradorData);
            setPromedio(estadoMasComun);
        }
    }, [colaboradorData]);

    useEffect(() => {
        if(selectedColaborador && !showOperator) {
            getHistoricData(setHistoricoData, selectedColaborador)
        } else if (selectedColaborador && showOperator) {
            individualOperatorAnalysis(setHistoricoData, selectedColaborador)
        }
    }, [selectedColaborador, showOperator])

    useEffect(() => {
        const transformedData = transformData(generalData);
        setTransformedData(transformedData);
    }, [generalData]);

    // Función para transformar los datos para que sean compatibles con Recharts
    const transformData = (rawData) => {
        const months = [
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ];

        const transformedData = [];

        months.forEach((month) => {
            const monthData = {
                name: month,
            };

            rawData.forEach(yearData => {
                monthData[`${yearData.año}`] = yearData[month];
            });

            transformedData.push(monthData);
        });

        return transformedData;
    };


    // Transformación de datos para las gráficas
    const colaboradoresMotivacionData = colaboradorData.reduce((acc, curr) => {
        if (!acc[curr.PROM_MOTIVACIÓN]) {
            acc[curr.PROM_MOTIVACIÓN] = 0;
        }
        acc[curr.PROM_MOTIVACIÓN]++;
        return acc;
    }, {});

    const motivacionPieData = Object.keys(colaboradoresMotivacionData).map(key => ({
        name: key,
        value: colaboradoresMotivacionData[key]
    }));

    const departamentosBarData = departamentoData.map(item => ({
        departamento: item.DEPARTAMENTO,
        motivacion: getMotivacionValue(item.PROM_MOTIVACIÓN),
        sueño: getDreamValue(item.PROM_SUEÑO)
    }));

    // Opciones para el selector de colaboradores
    const colaboradorOptions = colaboradorData.map(col => ({
        label: col.NOMBRE,
        value: col.ID
    }));

    // Función para obtener valor numérico de motivación para gráficas
    function getMotivacionValue(motivacion) {
        const values = {
            'MOLESTO': 1,
            'ANSIOSO': 2,
            'TRISTE': 3,
            'TRANQUILO': 4,
            'MOTIVADO': 5,
            'FELIZ': 6
        };
        return values[motivacion] || 0;
    }

    function getMotivacionLabel(valor) {
        const values = {
            1: 'MOLESTO',
            2: 'ANSIOSO',
            3: 'TRISTE',
            4: 'TRANQUILO',
            5: 'MOTIVADO',
            6: 'FELIZ'
        };

        // Redondeas al entero más cercano
        return values[Math.round(valor)] || 'DESCONOCIDO';
    }

    // Función para obtener valor numérico de sueño para gráficas
    function getDreamValue(dream) {
        const values = {
            '5 o menos': 1,
            '6': 2,
            '7': 3,
            '8': 4,
            '9 o más': 5
        };
        return values[dream] || 0;
    }

    const obtenerEstadoPredominante = (datos) => {
        const conteo = datos.reduce((acc, item) => {
            const estado = item.PROM_MOTIVACIÓN;
            acc[estado] = (acc[estado] || 0) + 1;
            return acc;
        }, {});

        // Obtener el estado con mayor conteo
        let estadoPredominante = null;
        let max = 0;
        for (const estado in conteo) {
            if (conteo[estado] > max) {
                max = conteo[estado];
                estadoPredominante = estado;
            }
        }

        return estadoPredominante;
    };

    // Agrupar por estado de ánimo y contar
    const moodData = useMemo(() => {
        const moodCount = {};

        departamentoData.forEach(item => {
            const mood = item["PROM_MOTIVACIÓN"];
            if (moodCount[mood]) {
                moodCount[mood]++;
            } else {
                moodCount[mood] = 1;
            }
        });

        // Convertir a formato para PieChart
        return Object.keys(moodCount).map(mood => ({
            name: mood,
            value: moodCount[mood]
        }));
    }, [departamentoData]);

    // Calcular el total para los porcentajes
    const total = useMemo(() => {
        return moodData.reduce((sum, item) => sum + item.value, 0);
    }, [moodData]);


    const agruparPorDepartamento = useMemo(() => {
        const grupos = {};
        colaboradorData.forEach((item) => {
            if (!grupos[item.DEPARTAMENTO]) {
                grupos[item.DEPARTAMENTO] = [];
            }
            grupos[item.DEPARTAMENTO].push(item);
        });

        return Object.keys(grupos).map((dep) => ({
            departamento: dep,
            empleados: grupos[dep]
        }));
    }, [colaboradorData])


// Colores para cada año
    const colors = ['#8884d8', '#82ca9d', '#ffc658'];


    // Colores para las gráficas
    const COLORS = ['#ff6384', '#36a2eb', '#ffce56', '#4bc0c0', '#9966ff', '#ff9f40'];

    // Colores para el estado de ánimo
    const motivacionColors = {
        'FELIZ': '#4CAF50',
        'MOTIVADO': '#8BC34A',
        'TRANQUILO': '#03A9F4',
        'TRISTE': '#9E9E9E',
        'ANSIOSO': '#FF9800',
        'MOLESTO': '#F44336'
    };

    const CustomTooltip2 = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="font-bold">{label}</p>
                    {payload.map((entry, index) => {
                        const motivacionLabel = getMotivacionLabel(entry.value);
                        return (
                            <p key={index} style={{ color: entry.color }} className="text-sm">
                                <span className="font-medium">{entry.name}: </span>
                                {motivacionLabel}
                            </p>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    // Formatear leyenda para mostrar etiquetas en lugar de números
    const formatLegendValue = (value) => {
        return `Año ${value}`;
    };

    const [filters, setFilters] = useState({
        NOMBRE: { value: null, matchMode: FilterMatchMode.CONTAINS },
        DEPARTAMENTO: { value: null, matchMode: FilterMatchMode.CONTAINS },
        PROM_MOTIVACIÓN: { value: null, matchMode: FilterMatchMode.EQUALS },
        PROM_SUEÑO: { value: null, matchMode: FilterMatchMode.EQUALS }
    });

    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'] = { value, matchMode: FilterMatchMode.CONTAINS };
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const renderHeader = () => {
        return (
            <div className="table-header">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search"> </InputIcon>
                    <InputText
                        value={globalFilterValue}
                        onChange={onGlobalFilterChange}
                        placeholder="Buscar..."
                    />
                </IconField>

                <Button label="Descargar excel" icon="pi pi-file-excel" className="p-button-success" onClick={exportCSV} />
            </div>
        );
    };


    const motivacionTemplate = (rowData) => {
        return (
            <div className="motivacion-indicator">
                <div
                    className="indicator-dot"
                    style={{ backgroundColor: motivacionColors[rowData.PROM_MOTIVACIÓN] }}
                ></div>
                <span>{rowData.PROM_MOTIVACIÓN}</span>
            </div>
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const percentage = (payload[0].value / total) * 100;
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-title">{`${payload[0].name}: ${payload[0].value} departamento(s)`}</p>
                    <p className="tooltip-text">{`${percentage.toFixed(1)}% del total`}</p>
                </div>
            );
        }
        return null;
    };

    const rowExpansionTemplate = (rowData) => {
        const empleadosDepartamento = colaboradorData.filter(
            (e) => e.DEPARTAMENTO === rowData.DEPARTAMENTO
        );

        return (
            <div className="p-3">
                <DataTable value={empleadosDepartamento} responsiveLayout="scroll">
                    <Column field="NOMBRE" header="Nombre" />
                    <Column field="PUESTO" header="Puesto" />
                    <Column field="PROM_MOTIVACIÓN" header="Motivación" body={motivacionTemplate} />
                    <Column field="PROM_SUEÑO" header="Sueño" />
                </DataTable>
            </div>
        );
    };

    return (
        <div className="dashboard-container">
            <SelectButton value={showOperator}
                          onChange={(e) => setShowOperator(e.value)}
                          options={show}
                          optionLabel="name"
                          optionValue="value"
                          style={{marginBottom: '1.5em'}}
            />
            <div className="kpi-cards">
                {/* KPI Cards */}
                <Card className="kpi-card">
                    <div className="kpi-content">
                        <h3 className="kpi-title">Total Colaboradores</h3>
                        <p className="kpi-value kpi-blue">{colaboradorData.length}</p>
                    </div>
                </Card>
                {!showOperator && (
                    <Card className="kpi-card">
                        <div className="kpi-content">
                            <h3 className="kpi-title">Departamentos</h3>
                            <p className="kpi-value kpi-green">{departamentoData.length}</p>
                        </div>
                    </Card>
                )}
                <Card className="kpi-card">
                    <div className="kpi-content">
                        <h3 className="kpi-title">Estado Promedio</h3>
                        <p className="kpi-value kpi-purple">{promedio}</p>
                    </div>
                </Card>
            </div>
            <Card title="Tendencia de Motivación por Mes y Año" className="dept-chart-card">
                <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={transformedData}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis
                                domain={[0, 6]}
                                ticks={[1, 2, 3, 4, 5, 6]}
                                tickFormatter={(value) => getMotivacionLabel(value).substring(0, 3)}
                            />
                            <Tooltip content={<CustomTooltip2 />} />
                            <Legend formatter={formatLegendValue} />
                            {generalData.map((yearData, index) => (
                                <Line
                                    key={yearData.año}
                                    type="monotone"
                                    dataKey={`${yearData.año}`}
                                    stroke={colors[index % colors.length]}
                                    activeDot={{ r: 8 }}
                                    name={`${yearData.año}`}
                                    connectNulls
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="dashboard-tabs">
                <TabPanel header="Visión General">
                    <div className="grid-layout">
                        <Card title="Estado Anímico por Colaborador" className="card-container">
                            <DataTable
                                ref={dt}
                                size="small"
                                value={colaboradorData}
                                paginator
                                rows={5}
                                filters={filters}
                                header={renderHeader()}
                                className="p-datatable-sm p-datatable-responsive"
                                emptyMessage="No hay datos disponibles"
                            >
                                <Column
                                    field="NOMBRE"
                                    header="Nombre"
                                    sortable
                                />
                                {!showOperator && (
                                    <Column
                                        field="DEPARTAMENTO"
                                        header="Departamento"
                                        sortable
                                    />
                                )}
                                <Column
                                    field="PROM_MOTIVACIÓN"
                                    header="Motivación"
                                    body={motivacionTemplate}
                                    sortable
                                />
                                <Column
                                    field="PROM_SUEÑO"
                                    header="Horas de Sueño"
                                    sortable
                                />
                            </DataTable>
                        </Card>

                        <Card title="Distribución de Estados Anímicos" className="card-container">
                            <div className="chart-container">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={motivacionPieData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                            label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {motivacionPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                        {!showOperator && (
                            <>
                                <Card title="Estado Anímico por Departamento" className="card-container">
                                    <div className="chart-container">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={departamentosBarData}
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="departamento"
                                                    tick={{ fontSize: 8 }}
                                                    angle={-45}
                                                    textAnchor="end"
                                                    height={70}
                                                />
                                                <YAxis
                                                    domain={[1, 6]}
                                                    ticks={[1, 2, 3, 4, 5, 6]}
                                                    tickFormatter={(value) => getMotivacionLabel(value).substring(0, 3)}
                                                />
                                                <Tooltip
                                                    formatter={(value, name) => {
                                                        if (name === 'Nivel de Motivación') {
                                                            const levels = ['', 'MOLESTO', 'ANSIOSO', 'TRISTE', 'TRANQUILO', 'MOTIVADO', 'FELIZ'];
                                                            return levels[value];
                                                        }
                                                        if (name === 'Calidad de Sueño') {
                                                            const sleepLevels = ['', '5 o menos', '6', '7', '8', '9 o más'];
                                                            return sleepLevels[value];
                                                        }
                                                        return value;
                                                    }}
                                                    labelFormatter={(departamento) => departamento}
                                                />
                                                <Legend />
                                                <Bar dataKey="motivacion" name="Nivel de Motivación" fill="#8884d8" />
                                                <Bar dataKey="sueño" name="Calidad de Sueño" fill="#82ca9d" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>

                                <Card title="Nivel de Sueño vs. Motivación" className="card-container">
                                    <div className="chart-container">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                layout="vertical"
                                                data={departamentosBarData}
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis type="number" domain={[0, 6]} />
                                                <YAxis dataKey="departamento" type="category" tick={{ fontSize: 10 }} />
                                                <Tooltip
                                                    formatter={(value, name) => {
                                                        if (name === 'Nivel de Motivación') {
                                                            const levels = ['', 'MOLESTO', 'ANSIOSO', 'TRISTE', 'TRANQUILO', 'MOTIVADO', 'FELIZ'];
                                                            return levels[value];
                                                        }
                                                        if (name === 'Horas de Sueño') {
                                                            const sleepLevels = ['', '5 o menos', '6', '7', '8', '9 o más'];
                                                            return sleepLevels[value];
                                                        }
                                                        return value;
                                                    }}
                                                    labelFormatter={(departamento) => departamento}
                                                />
                                                <Legend />
                                                <Bar dataKey="sueño" name="Horas de Sueño" fill="#ff9f40" />
                                                <Bar dataKey="motivacion" name="Nivel de Motivación" fill="#36a2eb" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>
                            </>
                        )}
                    </div>
                </TabPanel>

                <TabPanel header="Análisis Individual">
                    <Card title="Seleccionar Colaborador" className="selector-container">
                        <Dropdown
                            value={selectedColaborador?.ID}
                            options={colaboradorOptions}
                            filter
                            onChange={(e) => {
                                const selected = colaboradorData.find(col => col.ID === e.value);
                                setSelectedColaborador(selected);
                            }}
                            placeholder="Seleccione un colaborador"
                            className="colaborador-dropdown"
                        />
                    </Card>

                    {selectedColaborador && (
                        <div className="profile-grid">
                            <Card className="profile-card">
                                <div className="profile-content">
                                    <h3 className="profile-name">{selectedColaborador.NOMBRE}</h3>
                                    <div className="profile-details">
                                        <div className="profile-item">
                                            <p className="profile-label">Departamento:</p>
                                            <p className="profile-value">{showOperator ? 'No aplica' :selectedColaborador.DEPARTAMENTO}</p>
                                        </div>
                                        <div className="profile-item">
                                            <p className="profile-label">Puesto:</p>
                                            <p className="profile-value">{showOperator ? 'Operador' :selectedColaborador.PUESTO}</p>
                                        </div>
                                        <div className="profile-item">
                                            <p className="profile-label">Estado Anímico:</p>
                                            <p className="profile-value" style={{ color: motivacionColors[selectedColaborador.PROM_MOTIVACIÓN] }}>
                                                {selectedColaborador.PROM_MOTIVACIÓN}
                                            </p>
                                        </div>
                                        <div className="profile-item">
                                            <p className="profile-label">Horas de Sueño:</p>
                                            <p className="profile-value">{selectedColaborador.PROM_SUEÑO}</p>
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            <Card title="Histórico de Estado Anímico" className="history-card">
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={historicoData}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                                            <YAxis domain={[1, 6]} ticks={[1, 2, 3, 4, 5, 6]} tick={{ fontSize: 10 }} tickFormatter={(value) => {
                                                const labels = ['', 'MOLESTO', 'ANSIOSO', 'TRISTE', 'TRANQUILO', 'MOTIVADO', 'FELIZ'];
                                                return labels[value] || '';
                                            }} />
                                            <Tooltip
                                                formatter={(value) => {
                                                    const labels = ['', 'MOLESTO', 'ANSIOSO', 'TRISTE', 'TRANQUILO', 'MOTIVADO', 'FELIZ'];
                                                    return labels[value] || value;
                                                }}
                                                contentStyle={{ fontSize: 12 }}
                                            />
                                            <Legend/>
                                            <Line type="monotone" dataKey="valor" name="Estado Anímico" stroke="#8884d8" activeDot={{ r: 8 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </div>
                    )}
                </TabPanel>

                {!showOperator && (
                    <TabPanel header="Departamentos">
                        <Card title="Comparativa por Departamentos" className="dept-table-container">
                            <DataTable
                                value={departamentoData}
                                expandedRows={expandedRows}
                                onRowToggle={(e) => setExpandedRows(e.data)}
                                rowExpansionTemplate={rowExpansionTemplate}
                                dataKey="DEPARTAMENTO"
                                emptyMessage="No hay datos disponibles"
                            >
                                <Column expander style={{ width: "3em" }} />
                                <Column field="DEPARTAMENTO" header="Departamento" sortable />
                                <Column field="PROM_MOTIVACIÓN" header="Motivación Promedio" body={motivacionTemplate} sortable />
                                <Column field="PROM_SUEÑO" header="Horas de Sueño Promedio" sortable />
                            </DataTable>
                        </Card>

                        <div className="dept-grid">
                            <Card title="Estado Anímico por Departamento" className="dept-chart-card">
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={moodData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={true}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                                nameKey="name"
                                                label={({ name, value }) => `${name}: ${((value / total) * 100).toFixed(1)}%`}
                                            >
                                                {moodData.map((entry) => (
                                                    <Cell
                                                        key={`cell-${entry.name}`}
                                                        fill={motivacionColors[entry.name] || "#999999"}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card title="Relación Sueño-Motivación por Departamento" className="dept-chart-card">
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={departamentosBarData}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="departamento"
                                                tick={{ fontSize: 8 }}
                                                angle={-45}
                                                textAnchor="end"
                                                height={70}
                                            />
                                            <YAxis
                                                domain={[1, 6]}
                                                ticks={[1, 2, 3, 4, 5, 6]}
                                                tickFormatter={(value) => getMotivacionLabel(value).substring(0, 3)}
                                            />
                                            <Tooltip
                                                formatter={(value, name) => {
                                                    if (name === 'Nivel de Motivación') {
                                                        const levels = ['', 'MOLESTO', 'ANSIOSO', 'TRISTE', 'TRANQUILO', 'MOTIVADO', 'FELIZ'];
                                                        return levels[value];
                                                    }
                                                    if (name === 'Calidad de Sueño') {
                                                        const sleepLevels = ['', '5 o menos', '6', '7', '8', '9 o más'];
                                                        return sleepLevels[value];
                                                    }
                                                    return value;
                                                }}
                                                labelFormatter={(departamento) => departamento}
                                            />
                                            <Legend />
                                            <Bar dataKey="motivacion" name="Nivel de Motivación" fill="#8884d8" />
                                            <Bar dataKey="sueño" name="Calidad de Sueño" fill="#82ca9d" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </div>
                    </TabPanel>
                )}
            </TabView>
        </div>
    );
}

export default ClimaLaboral
